import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { IUpdateRawQueryListApiModel } from '@models/Update.model';
import { differenceInSeconds, parseISO } from 'date-fns';

export interface INewUpdates {
  update: IUpdateRawQueryListApiModel;
  date: string;
}

interface IDeletedUpdates {
  updateId: string;
  date: string;
}

export const updatesOptimisticSlice = createSlice({
  name: 'updatesOptimistic',
  initialState: {
    newUpdates: [] as INewUpdates[],
    updatedUpdates: [] as INewUpdates[],
    backupUpdatedUpdates: [] as INewUpdates[],
    backupNewUpdates: [] as INewUpdates[],
    deletedUpdates: [] as IDeletedUpdates[],
  },
  reducers: {
    addNewUpdate: (
      state,
      action: PayloadAction<{
        newUpdate: IUpdateRawQueryListApiModel;
        updatesMtViewTime: number;
      }>,
    ) => {
      const newList = state.newUpdates.filter(
        item =>
          Math.abs(differenceInSeconds(parseISO(item.date), new Date())) <
          action.payload.updatesMtViewTime,
      );

      newList.push({
        update: action.payload.newUpdate,
        date: new Date().toISOString(),
      });

      state.newUpdates = newList;
    },
    addBackupNewUpdate: (
      state,
      action: PayloadAction<IUpdateRawQueryListApiModel>,
    ) => {
      const now = new Date();
      const payloadReturn = {
        update: action.payload,
        date: now.toJSON(),
      };
      const newUpdates = state.backupNewUpdates.concat(payloadReturn);
      state.backupNewUpdates = newUpdates;
    },
    removeBackUpNewUpdates: (state, action: PayloadAction<string[]>) => {
      const filtered = state.backupNewUpdates.filter(notInList(action));
      state.backupNewUpdates = filtered;
    },
    addUpdatedUpdate: (
      state,
      action: PayloadAction<{
        newUpdate: IUpdateRawQueryListApiModel;
        updatesMtViewTime: number;
      }>,
    ) => {
      const findNewList = state.newUpdates.find(
        item =>
          item.update.id === action.payload.newUpdate.id &&
          Math.abs(differenceInSeconds(parseISO(item.date), new Date())) <
            action.payload.updatesMtViewTime,
      );
      // Update matview time for optimisic updates that were changed
      if (findNewList) {
        const newList = state.newUpdates.filter(
          item => item.update.id !== action.payload.newUpdate.id,
        );

        newList.push({
          update: action.payload.newUpdate,
          date: new Date().toISOString(),
        });
        // however, we don't update the updatedUpdate. Because it is unnecessary.
        state.newUpdates = newList;
      }
    },
    addBackUpUpdatedUpdates: (
      state,
      action: PayloadAction<IUpdateRawQueryListApiModel>,
    ) => {
      const isInUpdates = state.backupUpdatedUpdates.find(update => {
        return update.update.id === action.payload.id;
      });
      const isInNewUpdates = state.backupNewUpdates.find(update => {
        return update.update.id === action.payload.id;
      });
      const now = new Date();
      const payloadReturn = {
        update: action.payload,
        date: now.toJSON(),
      };
      // update in place
      const updated = state.backupUpdatedUpdates.map(update => {
        if (update.update.id === action.payload.id) return payloadReturn;
        return update;
      });
      if (!isInUpdates) {
        // or add in
        updated.push(payloadReturn);
      }
      // Update new optimitic updates so they "move"
      if (isInNewUpdates) {
        const newNewUpdates = state.backupNewUpdates.map(update => {
          if (update.update.id === payloadReturn.update.id) {
            return payloadReturn;
          }
          return update;
        });
        state.backupNewUpdates = newNewUpdates;
      }
      // set
      state.backupUpdatedUpdates = updated;
    },
    removeBackUpUpdatedUpdates: (state, action: PayloadAction<string[]>) => {
      const filtered = state.backupUpdatedUpdates.filter(notInList(action));
      state.backupUpdatedUpdates = filtered;
    },
    addDeletedUpdate: (
      state,
      action: PayloadAction<{ updateId: string; updatesMtViewTime: number }>,
    ) => {
      const newList = state.deletedUpdates.filter(
        item =>
          Math.abs(differenceInSeconds(parseISO(item.date), new Date())) <
          action.payload.updatesMtViewTime,
      );

      newList.push({
        updateId: action.payload.updateId,
        date: new Date().toISOString(),
      });

      state.deletedUpdates = newList;
    },
  },
});

export const {
  addDeletedUpdate,
  addUpdatedUpdate,
  addBackUpUpdatedUpdates,
  addBackupNewUpdate,
  removeBackUpUpdatedUpdates,
  removeBackUpNewUpdates,
} = updatesOptimisticSlice.actions;

export const { reducer: updatesOptimisticReducer } = updatesOptimisticSlice;
function notInList(action: PayloadAction<string[]>) {
  return (update: INewUpdates) => {
    return !action.payload.includes(update.update.id);
  };
}
